import { Link } from "react-router-dom";
import {
  ArrowRight,
  Activity,
  Brain,
  Clock,
  LifeBuoy,
  HeartPulse,
  Users,
  ShieldCheck,
  Waves,
  Stethoscope,
  MoveRight,
  Sparkles,
  Phone,
} from "lucide-react";
import Seo, { orgSchema, websiteSchema, faqSchema } from "@/components/Seo";
import HelpCta from "@/components/HelpCta";
import { FAQ } from "@/data/faq";

const topFaqs = FAQ.slice(0, 5);

const STATS = [
  {
    value: "#1",
    label: "Droga con mayor capacidad adictiva en Chile",
    source: "SENDA",
  },
  {
    value: "+50.000",
    label: "Consumidores problemáticos estimados en el país",
    source: "Datos SENDA",
  },
  {
    value: "80%",
    label: "Desarrolla dependencia en menos de 1 mes",
    source: "Evidencia clínica",
  },
];

const EFECTOS = [
  {
    icon: Waves,
    label: "Inmediatos",
    color: "text-red-400",
    bg: "bg-red-500/10",
    ring: "ring-red-500/20",
    items: [
      "Euforia intensa (8-15 min)",
      "Taquicardia y sudoración",
      "Bajón y angustia posterior",
    ],
  },
  {
    icon: Activity,
    label: "Corto plazo",
    color: "text-amber-300",
    bg: "bg-amber-500/10",
    ring: "ring-amber-500/20",
    items: [
      "Pérdida de apetito extrema",
      "Insomnio y paranoia",
      "Deterioro dental rápido",
    ],
  },
  {
    icon: Brain,
    label: "Largo plazo",
    color: "text-cyan-300",
    bg: "bg-cyan-500/10",
    ring: "ring-cyan-500/20",
    items: [
      "Daño neurológico permanente",
      "Desnutrición crónica",
      "Situación de calle",
    ],
  },
];

const TRATAMIENTOS = [
  {
    icon: Stethoscope,
    title: "Desintoxicación",
    desc: "7–21 días con supervisión médica. Manejo del síndrome de abstinencia.",
  },
  {
    icon: HeartPulse,
    title: "Ambulatorio",
    desc: "Terapia intensiva 2–5 sesiones por semana, sin internación.",
  },
  {
    icon: ShieldCheck,
    title: "Residencial",
    desc: "Internación 24/7 para dependencias severas. Programa 3–12 meses.",
  },
  {
    icon: Users,
    title: "Comunidad Terapéutica",
    desc: "Programa largo (6–18 meses) con reinserción social y reconstrucción de vínculos.",
  },
];

export default function Landing() {
  const schemas = [
    orgSchema,
    websiteSchema,
    faqSchema(topFaqs),
  ];

  return (
    <>
      <Seo
        title="Pasta Base en Chile: Información, Efectos, Tratamiento y Ayuda | pasta-base.cl"
        description="Información completa sobre la pasta base: qué es, efectos en el cuerpo y la mente, tipos de tratamiento, test de dependencia y dónde encontrar ayuda profesional en Chile."
        path="/"
        schema={schemas}
      />

      {/* HERO */}
      <section data-testid="hero" className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-grid-slate" />
        <div className="absolute inset-0 -z-10 spotlight" />
        <div className="absolute inset-x-0 top-0 -z-10 h-[600px] bg-gradient-to-b from-slate-950/0 via-slate-950/40 to-slate-950" />

        <div className="mx-auto max-w-7xl px-5 pt-20 pb-24 lg:px-8 lg:pt-28 lg:pb-32">
          <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
                <Sparkles className="h-3.5 w-3.5" />
                Información verificada · red SinAdicciones.org
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
                Pasta Base en Chile:
                <br />
                <span className="bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">
                  Todo lo que Necesitas Saber
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Información verificada, test de dependencia confidencial y acceso
                a tratamiento profesional real. Hecho para personas que viven
                esta realidad hoy.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-urgent"
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-red-500/25 hover:bg-red-600"
                >
                  <LifeBuoy className="h-4 w-4" />
                  Necesito Ayuda Ahora
                </a>
                <Link
                  to="/test-dependencia"
                  data-testid="hero-cta-test"
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
                >
                  Hacer Test de Dependencia
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
                <Phone className="h-4 w-4 text-red-400" />
                <span>
                  Fono Drogas SENDA:{" "}
                  <a href="tel:1412" className="font-semibold text-slate-100">
                    1412
                  </a>{" "}
                  · 24/7, gratuito
                </span>
              </div>
            </div>

            <div className="grid gap-4 fade-up delay-2">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur-sm transition hover:border-emerald-500/40 hover:bg-slate-900"
                >
                  <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-emerald-500/5 blur-2xl transition group-hover:bg-emerald-500/20" />
                  <div className="flex items-baseline gap-3">
                    <span className="bg-gradient-to-br from-emerald-300 to-cyan-300 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl">
                      {s.value}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-300">{s.label}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">
                    Fuente: {s.source}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es? */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                Introducción
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                ¿Qué es la pasta base?
              </h2>
            </div>
            <div className="space-y-5 text-slate-300">
              <p className="text-base leading-relaxed sm:text-lg">
                La pasta base —también llamada bazuco, paco, mono o angustia— es
                un producto intermedio en la elaboración de la cocaína. Se
                obtiene tratando las hojas de coca con solventes como kerosene,
                ácido sulfúrico y bicarbonato, lo que la convierte en una
                sustancia altamente tóxica e impura.
              </p>
              <p className="leading-relaxed">
                Se fuma en pipas artesanales. Llega al cerebro en menos de 8
                segundos y provoca una liberación masiva de dopamina, seguida de
                una caída abrupta que genera la necesidad compulsiva de volver a
                consumir. Por su bajo costo y efecto inmediato, es una de las
                drogas con mayor impacto social en Chile.
              </p>
              <Link
                to="/que-es-pasta-base"
                data-testid="link-que-es"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
              >
                Leer artículo completo
                <MoveRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Efectos */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                Efectos
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                Cómo afecta al cuerpo y a la mente
              </h2>
            </div>
            <Link
              to="/efectos"
              data-testid="link-efectos"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
            >
              Ver todos los efectos
              <MoveRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {EFECTOS.map((e) => {
              const Icon = e.icon;
              return (
                <article
                  key={e.label}
                  className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-6 transition hover:-translate-y-0.5 hover:border-slate-700"
                >
                  <div className={`mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg ring-1 ${e.bg} ${e.ring}`}>
                    <Icon className={`h-5 w-5 ${e.color}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    <h3 className="text-lg font-semibold text-slate-50">{e.label}</h3>
                  </div>
                  <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
                    {e.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-emerald-400" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Test preview */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-emerald-500/5 via-slate-900 to-cyan-500/5 p-8 sm:p-14">
            <div className="pointer-events-none absolute inset-0 bg-grid-slate opacity-40" />
            <div className="relative grid items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                  Evaluación confidencial
                </span>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                  ¿Crees que tú o alguien cercano tiene un problema con la
                  pasta base?
                </h2>
                <p className="mt-4 max-w-xl text-slate-300">
                  10 preguntas basadas en criterios clínicos DSM-5. Gratis,
                  anónimo, te da una orientación inmediata con pasos a seguir.
                </p>
                <div className="mt-7">
                  <Link
                    to="/test-dependencia"
                    data-testid="landing-test-cta"
                    className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
                  >
                    Hacer el Test de Dependencia
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Pregunta de ejemplo
                </p>
                <p className="mt-2 text-base font-medium text-slate-100">
                  "¿Has intentado dejar de consumir sin éxito?"
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  {["Nunca", "A veces", "Frecuentemente", "Siempre"].map((o, i) => (
                    <div
                      key={o}
                      className={`rounded-lg border px-3 py-2 ${
                        i === 2
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                          : "border-slate-800 bg-slate-950/50 text-slate-400"
                      }`}
                    >
                      {o}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tratamientos preview */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                Tratamiento
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                Tipos de tratamiento disponibles
              </h2>
            </div>
            <Link
              to="/tratamientos"
              data-testid="link-tratamientos"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
            >
              Conocer tratamientos
              <MoveRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TRATAMIENTOS.map((t) => {
              const Icon = t.icon;
              return (
                <article
                  key={t.title}
                  className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 transition hover:-translate-y-0.5 hover:border-emerald-500/40"
                >
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
                    <Icon className="h-5 w-5 text-emerald-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-50">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-5xl px-5 py-20 lg:px-8">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                Preguntas frecuentes
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                Lo que más se pregunta sobre la pasta base
              </h2>
            </div>
            <Link
              to="/preguntas-frecuentes"
              data-testid="link-faq"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
            >
              Ver todas las preguntas
              <MoveRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50">
            {topFaqs.map((f) => (
              <details key={f.id} className="group px-6 py-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-left text-base font-semibold text-slate-100 marker:hidden">
                  {f.question}
                  <span className="shrink-0 rounded-full bg-slate-800 p-1.5 text-slate-400 transition group-open:rotate-45 group-open:bg-emerald-500/10 group-open:text-emerald-300">
                    <ArrowRight className="h-4 w-4 -rotate-45 transition" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <HelpCta testidPrefix="landing" />
        </div>
      </section>
    </>
  );
}
